import { Box, Button, IconButton, MenuItem, Paper, Select, Stack, TextField, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ImageIcon from "@mui/icons-material/Image";
import { useState } from "react";
import { getPreSignedUrl } from "../../api/docs";
import { v4 as uuidv4 } from "uuid";

interface Props {
    block: CardBlock;
    onChange: (content: any) => void;
    onDelete: () => void;
}

const CardBlockEditor = ({ block, onChange, onDelete }: Props) => {
    const [uploading, setUploading] = useState(false);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const fileName = `page-images/${uuidv4()}-${file.name}`;
            const preSignedUrl = await getPreSignedUrl(fileName, file.type);
            
            await fetch(preSignedUrl, {
                method: "PUT",
                body: file,
                headers: {
                    "Content-Type": file.type,
                },
            });

            const imageUrl = `https://riverhawkoa-docs.s3.amazonaws.com/${fileName}`;
            onChange({
                ...block.content,
                image: imageUrl,
            });
        } catch (error) {
            console.error("Failed to upload image:", error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <IconButton size="small" sx={{ cursor: "grab" }}>
                    <DragIndicatorIcon />
                </IconButton>
                <Box flex={1}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                        Card Block
                    </Typography>
                    <Stack spacing={2}>
                        <TextField
                            label="Title"
                            value={block.content.title || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    title: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Description"
                            value={block.content.description || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    description: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                        />
                        {block.content.image ? (
                            <Box>
                                <img
                                    src={block.content.image}
                                    alt={block.content.title}
                                    style={{ maxWidth: "100%", height: "auto", maxHeight: 150 }}
                                />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    border: "2px dashed #ccc",
                                    borderRadius: 1,
                                    p: 2,
                                    textAlign: "center",
                                }}
                            >
                                <ImageIcon sx={{ fontSize: 32, color: "#ccc" }} />
                            </Box>
                        )}
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={uploading}
                            size="small"
                        >
                            {uploading ? "Uploading..." : "Upload Image"}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleImageUpload}
                            />
                        </Button>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">Image Position:</Typography>
                            <Select
                                value={block.content.imagePosition || "top"}
                                onChange={(e) =>
                                    onChange({
                                        ...block.content,
                                        imagePosition: e.target.value,
                                    })
                                }
                                size="small"
                            >
                                <MenuItem value="top">Top</MenuItem>
                                <MenuItem value="left">Left</MenuItem>
                                <MenuItem value="right">Right</MenuItem>
                            </Select>
                        </Stack>
                        <TextField
                            label="Button Text (optional)"
                            value={block.content.buttonText || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    buttonText: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Button URL"
                            value={block.content.buttonUrl || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    buttonUrl: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                    </Stack>
                </Box>
                <IconButton size="small" onClick={onDelete} color="error">
                    <DeleteIcon />
                </IconButton>
            </Stack>
        </Paper>
    );
};

export default CardBlockEditor;
